import { apiGet, apiPatch, apiPost } from '@/api/axios-client'
import type { Card } from '@/types/entities'
import { normalizeCard, normalizeCards } from './normalize'

/**
 * The one door a work item comes through.
 *
 * It exists because the same bug happened twice. A work item written by a code path that
 * omitted a column comes back with lists that are null or absent, and every component that
 * reads `approverIds.filter(...)` or `assigneeIds.map(...)` dies on it — correctly, by the
 * type, which promises those are arrays. The first fix normalized the board's query. The
 * drawer fetched its card by id through a different call, so opening that same item still
 * took the page down.
 *
 * Eight call sites was seven too many. Every one of them is here now, and every one
 * normalizes, so the next component to read a card list cannot get an unnormalized one —
 * there is nowhere else to get a card from.
 *
 * Still a seatbelt, not a fix: the fix is that nothing writes an incomplete row.
 */

export interface CardsResponse {
  items: Card[]
  pagination: { limit: number; nextCursor: string | null; hasMore: boolean }
}

export interface CardsQueryBody {
  filter: unknown
  limit?: number
  cursor?: string | null
}

export const cardsApi = {
  get: async (cardId: string): Promise<Card> => normalizeCard(await apiGet<Card>(`cards/${cardId}`)),

  /** Resolve current ids, TAP codes, and recoverable legacy Slack `crd_...` references. */
  resolveRef: async (ref: string): Promise<Card> =>
    normalizeCard(await apiGet<Card>(`cards/resolve-ref/${encodeURIComponent(ref)}`)),

  /** Kept for callers that explicitly have a permanent TAP reference. */
  getByCode: async (code: string): Promise<Card> =>
    normalizeCard(await apiGet<Card>(`cards/by-code/${encodeURIComponent(code)}`)),

  query: async (body: CardsQueryBody): Promise<CardsResponse> => {
    const raw = await apiPost<CardsResponse>('cards/query', body)
    return { ...raw, items: normalizeCards(raw.items) }
  },

  create: async (body: unknown): Promise<Card> => normalizeCard(await apiPost<Card>('cards', body)),

  /** One line, one sub-item: the server fills everything else in from the parent. */
  addSubItem: async (parentId: string, title: string): Promise<Card> =>
    normalizeCard(await apiPost<Card>(`cards/${parentId}/sub-items`, { title })),

  patch: async (cardId: string, body: unknown): Promise<Card> =>
    normalizeCard(await apiPatch<Card>(`cards/${cardId}`, body)),

  schedule: async (
    cardId: string,
    body: { startAt?: string | null; dueAt?: string | null; version?: number; timeZone?: string },
  ): Promise<Card> => normalizeCard(await apiPatch<Card>(`cards/${cardId}/schedule`, body)),

  approve: async (cardId: string): Promise<Card> =>
    normalizeCard(await apiPost<Card>(`cards/${cardId}/approve`, {})),

  redelegateApprover: async (
    cardId: string,
    body: { slot: 1 | 2; newApproverId: string; reason: string },
  ): Promise<Card> =>
    normalizeCard(await apiPost<Card>(`cards/${cardId}/redelegate-approver`, body)),

  watch: async (cardId: string, watching: boolean): Promise<Card> =>
    normalizeCard(await apiPost<Card>(`cards/${cardId}/watch`, { watching })),
}
